'use client';

import { defineStyleConfig, Flex, Grid, useStyleConfig, GridProps } from '@chakra-ui/react';

import Text from '@/components/atomic/v1/Text';
import Tooltip, { TooltipType } from '@/components/atomic/v1/tooltip/Tooltip';
import Icon, { IconProps } from '@/components/atomic/v1/icon/Icon';
import Tag, { TagProps } from '@/components/atomic/v1/tag/Tag';

export const dataSetTheme = defineStyleConfig({
  variants: {
    none: {
      backgroundColor: 'transparent'
    },
    white: {
      backgroundColor: 'background/white'
    },
    primary: {
      backgroundColor: 'background/primary'
    },
    secondary: {
      backgroundColor: 'background/secondary'
    }
  }
});

interface DataSetProps extends GridProps {
  variant: 'none' | 'white' | 'primary' | 'secondary';
  direction: 'horizontal' | 'vertical';
  label: string | React.ReactNode;
  children: React.ReactNode;
  tooltipProps?: Omit<TooltipType, 'tooltipText' | 'icon'>;
  iconProps?: IconProps;
  tagProps?: TagProps;
  hasBorder?: boolean;
}

const DataSet = ({
  variant,
  direction,
  iconProps,
  label,
  children,
  tooltipProps,
  tagProps,
  hasBorder,
  ...rest
}: DataSetProps) => {
  const styles = useStyleConfig('DataSet', { variant });

  return (
    <Grid
      gridTemplateColumns="auto 1fr"
      gridTemplateRows="min-content auto"
      rowGap={4}
      columnGap={direction === 'horizontal' ? 4 : 2}
      sx={styles}
      alignItems="start"
      borderStyle={hasBorder ? 'solid' : 'none'}
      borderWidth={hasBorder ? '2px' : 'none'}
      borderColor={hasBorder ? 'grey.40' : 'transparent'}
      borderRadius="8px"
      padding={variant === 'none' ? 0 : { base: 4, md: 6 }}
      {...rest}
    >
      {iconProps && (
        <Icon
          flexShrink={0}
          gridRow={direction === 'horizontal' ? '1 / 3' : '1 / 2'}
          color="icon/default"
          {...iconProps}
        />
      )}

      {tagProps && (
        <Tag marginLeft={direction === 'horizontal' ? 'initial' : 'auto'} width="fit-content" {...tagProps} />
      )}

      <Flex gridColumn={direction === 'horizontal' ? '2 / 3' : '1 / 3'} flexDirection="column" gap={3}>
        <Flex columnGap={2}>
          <Text fontWeight="bold">{label}</Text>
          {tooltipProps && <Tooltip tooltipText="" icon="question" {...tooltipProps} />}
        </Flex>

        {children}
      </Flex>
    </Grid>
  );
};

export default DataSet;
